/**
 * Booking source attribution — framework-agnostic helpers for tagging an
 * appointment with the entry point ("channel") it originated from.
 *
 * The resulting record is written to the opaque `Appointment.metadata` field
 * (string→string, see `appointmentMetadataSchema`) so a business can compare
 * where their bookings come from: their website or the subdomain booking
 * link.
 *
 * NOTE: `bookingChannel` is the rendering surface only. External entry points
 * such as "Reserve with Google" or Apple Maps link to the subdomain booking
 * page, so they resolve to `subdomain` here. The UTM tags are still captured
 * (`utmSource`/`utmMedium`/`utmCampaign`) so a future ad-management surface can
 * attribute those campaigns separately.
 *
 * Staff-side bookings are tagged too: `dashboard` (owner/staff booked it in
 * the dashboard) and `staff` (booked via the staff portal app). Reschedule
 * flows must CARRY OVER the previous appointment's metadata when the caller
 * does not supply a fresh record — cancel-and-replace reschedules otherwise
 * silently drop attribution.
 *
 * AI Receptionist bookings are tagged `voice`. The API writes that record
 * itself (`executeVoiceBook` in `apps/api/src/modules/svc/voiceWrites/book.ts`),
 * so `voice` has a label but no `BookingSurface`.
 *
 * Business websites and apps that book through the public API (Eddie's
 * Edition, Edition Studio, Haircuts by Lianne) write their own `integration`
 * key and no `bookingChannel`. `bookingChannelFromMetadata` reads the exact
 * values in `INTEGRATION_CHANNEL` as `website` or `native`, so those bookings
 * are not reported as untracked.
 *
 * Keep this free of PII — only surface/channel identifiers and UTM tags.
 */

/** The UI surface that rendered the booking flow. */
export type BookingSurface =
  | "subdomain-booking-page"
  | "website-template"
  | "dashboard-manual"
  | "staff-portal"
  | "reception";

/** Canonical entry point a booking is attributed to (for reporting). */
export type BookingChannel =
  | "website"
  | "subdomain"
  | "dashboard"
  | "staff"
  | "reception"
  | "voice"
  | "native";

/** Which first-party app produced the booking. */
export type BookingSourceApp =
  | "openings-web"
  | "openings-dashboard"
  | "openings-staff"
  | "openings-reception";

export interface BookingUtmParams {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
}

export interface BookingSourceInput {
  surface: BookingSurface;
  sourceApp: BookingSourceApp;
  utm?: BookingUtmParams | null;
}

/** Ordered list of channels for stable display in reports. */
export const BOOKING_CHANNELS: readonly BookingChannel[] = [
  "website",
  "subdomain",
  "dashboard",
  "staff",
  "reception",
  "voice",
  "native",
];

const SURFACE_CHANNEL: Record<BookingSurface, BookingChannel> = {
  "subdomain-booking-page": "subdomain",
  "website-template": "website",
  "dashboard-manual": "dashboard",
  "staff-portal": "staff",
  reception: "reception",
};

const CHANNEL_LABELS: Record<BookingChannel, string> = {
  website: "Website",
  subdomain: "Booking link",
  dashboard: "Dashboard",
  staff: "Staff app",
  reception: "Front desk",
  voice: "AI Receptionist",
  native: "Customer app",
};

/**
 * `metadata.integration` values written by business websites and apps that
 * predate `bookingChannel` (booking standard § 14 registry: a business website
 * is `website`, its native app is `native`). Exact values only — never infer a
 * channel from the shape of an unknown value.
 */
const INTEGRATION_CHANNEL: ReadonlyMap<string, BookingChannel> = new Map([
  ["eddies-edition-website", "website"],
  ["edition-studio-website", "website"],
  ["haircutsbylianne-website", "website"],
  ["eddies-edition-mobile-app", "native"],
]);

/**
 * Parse UTM params from a query string or URLSearchParams. Accepts a raw
 * `?a=b` string, a bare `a=b` string, or a `URLSearchParams` instance.
 */
export function parseUtmParams(
  input: string | URLSearchParams | null | undefined,
): BookingUtmParams {
  if (!input) return {};
  const params = typeof input === "string" ? new URLSearchParams(input) : input;
  const get = (key: string) => {
    const value = params.get(key);
    return value && value.trim() ? value.trim() : undefined;
  };
  return {
    source: get("utm_source"),
    medium: get("utm_medium"),
    campaign: get("utm_campaign"),
  };
}

/** Resolve the canonical booking channel from the rendering surface. */
export function resolveBookingChannel(
  input: BookingSourceInput,
): BookingChannel {
  return SURFACE_CHANNEL[input.surface];
}

/**
 * Whether a stored channel string is one this module names. The value comes
 * from caller-written metadata, so an inherited key such as "constructor"
 * must not count.
 */
export function isBookingChannel(channel: string): channel is BookingChannel {
  return Object.prototype.hasOwnProperty.call(CHANNEL_LABELS, channel);
}

/** Human-readable label for a channel (for dashboard display). */
export function bookingChannelLabel(channel: string): string {
  return isBookingChannel(channel) ? CHANNEL_LABELS[channel] : channel;
}

function trimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Extract the booking channel from an appointment's opaque `metadata` value
 * (unknown JSON from the DB). Returns the raw `bookingChannel` string — which
 * may be a value outside `BookingChannel` if written by a future/external
 * caller — or, without one, the channel of a known `integration` value
 * (`INTEGRATION_CHANNEL`). Null when the record carries no attribution.
 */
export function bookingChannelFromMetadata(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null;
  }
  const record = metadata as Record<string, unknown>;
  const channel = trimmedString(record.bookingChannel);
  if (channel) return channel;
  const integration = trimmedString(record.integration);
  return integration ? (INTEGRATION_CHANNEL.get(integration) ?? null) : null;
}

/**
 * Build the opaque metadata record describing where a booking came from.
 * `bookingChannel` reflects the surface; UTM keys are included only when
 * present (for later campaign attribution). The result satisfies
 * `appointmentMetadataSchema` (string→string, ≤16 keys, values ≤500 chars).
 */
export function buildBookingSourceMetadata(
  input: BookingSourceInput,
): Record<string, string> {
  const metadata: Record<string, string> = {
    bookingChannel: resolveBookingChannel(input),
    bookingSurface: input.surface,
    sourceApp: input.sourceApp,
  };
  const source = input.utm?.source?.trim();
  const medium = input.utm?.medium?.trim();
  const campaign = input.utm?.campaign?.trim();
  if (source) metadata.utmSource = source.slice(0, 500);
  if (medium) metadata.utmMedium = medium.slice(0, 500);
  if (campaign) metadata.utmCampaign = campaign.slice(0, 500);
  return metadata;
}
